import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const fmt = (d) => d.toISOString().slice(0, 10);
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

export default function Tracker() {
  const today = useMemo(() => new Date(), []);
  const [selectedDate, setSelectedDate] = useState(fmt(today));
  const [monthCursor, setMonthCursor] = useState(startOfMonth(today));
  const [supplements, setSupplements] = useState([]);
  const [logs, setLogs] = useState([]); // [{date, supplement_key, taken}]
  const [loading, setLoading] = useState(true);

  const loadSupplements = async () => {
    try {
      const recs = await api.get("/recommendations");
      const map = new Map();
      recs.data.forEach((r) =>
        r.supplements.forEach((s) => {
          if (!map.has(s.key)) map.set(s.key, { key: s.key, name: s.name });
        })
      );
      setSupplements([...map.values()]);
    } catch {
      /* ignore */
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      const start = fmt(startOfMonth(monthCursor));
      const endDate = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0);
      const { data } = await api.get("/tracker/logs", {
        params: { start, end: fmt(endDate) },
      });
      setLogs(data);
    } catch {
      toast.error("Could not load logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSupplements();
  }, []);
  useEffect(() => {
    loadLogs();
  }, [monthCursor]);

  const takenSet = useMemo(() => {
    const m = {};
    logs.forEach((l) => {
      if (l.taken) {
        m[l.date] = m[l.date] || new Set();
        m[l.date].add(l.supplement_key);
      }
    });
    return m;
  }, [logs]);

  const toggle = async (supp) => {
    const dateSet = takenSet[selectedDate] || new Set();
    const already = dateSet.has(supp.key);
    try {
      await api.post("/tracker/log", {
        date: selectedDate,
        supplement_key: supp.key,
        supplement_name: supp.name,
        taken: !already,
      });
      await loadLogs();
    } catch {
      toast.error("Could not save");
    }
  };

  // Build calendar grid
  const cal = useMemo(() => {
    const first = startOfMonth(monthCursor);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(monthCursor.getFullYear(), monthCursor.getMonth(), d));
    }
    return cells;
  }, [monthCursor]);

  const selectedSet = takenSet[selectedDate] || new Set();
  const monthLabel = monthCursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <main className="max-w-5xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="tracker-page">
      <header className="mb-10">
        <p className="label-eyebrow mb-2">Daily tracker</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight">Stay consistent, gently.</h1>
        <p className="mt-3 max-w-xl" style={{ color: "var(--text-2)" }}>
          Mark which supplements you actually took. Watch your streak grow.
        </p>
      </header>

      <div className="grid lg:grid-cols-5 gap-8">
        {/* Calendar */}
        <section className="card-soft p-6 lg:col-span-2" data-testid="calendar">
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={() => setMonthCursor(addMonths(monthCursor, -1))}
              className="btn-ghost p-2"
              data-testid="cal-prev"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <p className="font-serif text-xl">{monthLabel}</p>
            <button
              onClick={() => setMonthCursor(addMonths(monthCursor, 1))}
              className="btn-ghost p-2"
              data-testid="cal-next"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1 text-center">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
              <span key={i} className="label-eyebrow py-1">{d}</span>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cal.map((d, i) => {
              if (!d) return <div key={i} />;
              const dStr = fmt(d);
              const isSelected = dStr === selectedDate;
              const isToday = dStr === fmt(today);
              const hasLog = takenSet[dStr] && takenSet[dStr].size > 0;
              return (
                <button
                  key={dStr}
                  onClick={() => setSelectedDate(dStr)}
                  className="aspect-square rounded-xl text-sm flex flex-col items-center justify-center relative transition-colors"
                  style={{
                    background: isSelected ? "var(--brand)" : "transparent",
                    color: isSelected ? "#FDFBF7" : "var(--text)",
                    border: isToday && !isSelected ? "1px solid var(--brand)" : "1px solid transparent",
                  }}
                  data-testid={`cal-day-${dStr}`}
                >
                  <span>{d.getDate()}</span>
                  {hasLog && (
                    <span
                      className="w-1.5 h-1.5 rounded-full mt-0.5"
                      style={{ background: isSelected ? "#FDFBF7" : "var(--accent)" }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* Supplements for selected date */}
        <section className="lg:col-span-3">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <p className="label-eyebrow mb-1">Logging for</p>
              <p className="font-serif text-2xl">
                {new Date(selectedDate).toLocaleDateString(undefined, { dateStyle: "full" })}
              </p>
            </div>
            <p className="text-sm" style={{ color: "var(--text-2)" }}>
              {selectedSet.size} taken
            </p>
          </div>

          {loading ? (
            <p style={{ color: "var(--text-2)" }}>Loading…</p>
          ) : supplements.length === 0 ? (
            <div className="card-soft p-8 text-center">
              <p className="font-serif text-xl mb-2">No supplements to track yet</p>
              <p className="text-sm" style={{ color: "var(--text-2)" }}>
                Complete an assessment first — your recommended supplements will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {supplements.map((s) => {
                const taken = selectedSet.has(s.key);
                return (
                  <button
                    key={s.key}
                    onClick={() => toggle(s)}
                    className={`selectable-card w-full flex items-center justify-between ${taken ? "selected" : ""}`}
                    data-testid={`track-${s.key}`}
                  >
                    <span className="font-serif text-lg">{s.name}</span>
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{
                        background: taken ? "var(--brand)" : "transparent",
                        border: `1px solid ${taken ? "var(--brand)" : "var(--border)"}`,
                      }}
                    >
                      {taken && <Check className="w-4 h-4 text-white" strokeWidth={2} />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
